import { z } from 'zod';
import { REPORT_FREQS, REPORT_METRICS, TARGET_TYPES } from '../constants.ts';
import { idSchema, unixMsSchema } from './common.ts';

// Scheduled Slack digests (Sprint 8, docs/03 §Report schedules). scope_type reuses the
// schedule TARGET_TYPES enum (screen|group|org) — same specificity concept, a different
// table. metrics is metrics_json in the DB; the wire shape is a plain string array.

export const reportScopeTypeSchema = z.enum(TARGET_TYPES);
export const reportFreqSchema = z.enum(REPORT_FREQS);
export const reportMetricSchema = z.enum(REPORT_METRICS);

export const reportScheduleSchema = z.object({
  id: idSchema,
  org_id: idSchema,
  name: z.string().min(1),
  scope_type: reportScopeTypeSchema,
  scope_id: idSchema.nullable(), // null for scope_type=org
  freq: reportFreqSchema,
  day_of_week: z.number().int().min(0).max(6).nullable(), // 0=Mon…6=Sun, weekly only
  hour: z.number().int().min(0).max(23), // in `timezone`
  timezone: z.string().min(1),
  metrics: z.array(reportMetricSchema),
  channel: z.literal('slack'),
  slack_target: z.string().nullable(), // Slack channel id, or null = DM the creator
  enabled: z.boolean(),
  last_run_at: unixMsSchema.nullable(),
  created_by: idSchema.nullable(),
  created_at: unixMsSchema,
});
export type ReportSchedule = z.infer<typeof reportScheduleSchema>;

export const createReportScheduleSchema = z
  .object({
    name: z.string().min(1).max(200),
    scope_type: reportScopeTypeSchema,
    scope_id: idSchema.nullable().default(null),
    freq: reportFreqSchema,
    day_of_week: z.number().int().min(0).max(6).nullable().default(null),
    hour: z.number().int().min(0).max(23),
    timezone: z.string().min(1),
    metrics: z.array(reportMetricSchema).min(1),
    slack_target: z.string().nullable().default(null),
  })
  .refine((s) => s.scope_type === 'org' || s.scope_id !== null, {
    message: 'scope_id is required unless scope_type is "org"',
    path: ['scope_id'],
  })
  .refine((s) => s.freq !== 'weekly' || s.day_of_week !== null, {
    message: 'day_of_week is required when freq is "weekly"',
    path: ['day_of_week'],
  });
export type CreateReportSchedule = z.infer<typeof createReportScheduleSchema>;

// PATCH — all fields optional; cross-field rules (weekly→day_of_week, scope_id
// required unless org) are re-checked in the service against the MERGED result,
// same convention as updateScheduleSchema.
export const updateReportScheduleSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  scope_type: reportScopeTypeSchema.optional(),
  scope_id: idSchema.nullable().optional(),
  freq: reportFreqSchema.optional(),
  day_of_week: z.number().int().min(0).max(6).nullable().optional(),
  hour: z.number().int().min(0).max(23).optional(),
  timezone: z.string().min(1).optional(),
  metrics: z.array(reportMetricSchema).min(1).optional(),
  slack_target: z.string().nullable().optional(),
  enabled: z.boolean().optional(),
});
export type UpdateReportSchedule = z.infer<typeof updateReportScheduleSchema>;
